import { PromotionInfo } from './api.interface';
export interface ProductDetailResponse {
  success: boolean;
  code: string;
  msg: string | null;
  data: ProductDetailData;
  traceId: string;
}

export interface ProductDetailData {
  floors: Floor[];
  localInfo: LocalInfo;
}

export interface LocalInfo {
  countryCode: string;
  languageCode: string;
  currencyCode: string;
  currency: string;
  timezoneId: string;
  currencySymbol: string;
}

export interface Floor {
  floorId: string;
  data: FloorData | null;
  order: number;
}

export interface NavigationButtons {
  expand: Button[];
  collapse: Button[];
}

export interface BottomBarButtons {
  primaryBtns: Array<{
    btnId: string;
    btnName: string;
    btnNameKey: string;
    position: string;
    status: string;
    alwaysUsable: boolean | null;
  }>;
}

// 新增：优惠券信息接口
export interface CouponInfo {
  'C-P#couponInfo&couponBasic': {
    formattedBeginTime: string;
    couponName: string;
    formattedEndTime: string;
    quota: string;
    receivedTime: number;
    discount: string;
    beginTime: number;
    endTime: number;
    batchId: string;
    couponLimitType: number;
    couponStyle: number;
  };
  'C-P#couponInfo&couponCore': {
    thresholdPrice: string;
    couponType: number;
    couponId: string;
    useType: number;
    guideHit: number;
  };
}

// 新增：保障信息接口
export interface GuaranteeInfo {
  overview: string;
  items: Array<{
    type: {
      namespace: string;
      value: string;
      version: string;
      typeClass: string;
      typeSet: string[];
    };
    title: string;
    guaranteeType: number | null;
    logoUrl: string;
    verdorId: string | null;
    id: string;
    content: string;
    version: string;
  }>;
}

// 新增：评价销量数据楼层接口
export interface ProductCommentSaleFloor {
  // 匹配实际JSON字段
  saleAmount?: string; // 实际JSON使用 "saleAmount": "10万+"
  starShow?: boolean; // 实际JSON使用 "starShow": true
  star?: number; // 实际JSON使用 "star": 4.8
  commentCount?: number; // 实际JSON使用 "commentCount": 12345

  // 保持向下兼容的旧字段
  averageRating?: number; // 兼容旧版本，映射到 star
  saleCount?: string; // 兼容旧版本，映射到 saleAmount
  commentSummary?: {
    totalCount: number;
    positiveRate: string;
    ratingDistribution: Array<{
      rating: number;
      count: number;
      percentage: string;
    }>;
  };
}

// 新增：资产信息楼层接口（优惠券、礼品、样品等）
export interface ProductAssetsFloor {
  couponList?: CouponInfo[];
  giftList?: Array<{
    giftId: string;
    giftName: string;
    giftImage: string;
    giftDesc: string;
    giftType: string;
  }>;
  sampleList?: Array<{
    sampleId: string;
    sampleName: string;
    sampleImage: string;
    sampleDesc: string;
    availableCount: number;
  }>;
  guaranteeInfo?: GuaranteeInfo;
}

// 新增：SKU选择信息楼层接口
export interface ProductSelectedFloor {
  // 匹配实际JSON字段 - 特殊字段名需要用引号包围
  'C-M#viewSelectedItem&core'?: any; // 实际JSON中的特殊字段
  colorSizePartList?: Array<{
    // 实际JSON中的颜色尺寸列表
    colorId?: string;
    colorName?: string;
    sizeId?: string;
    sizeName?: string;
    stock?: number;
    price?: string;
  }>;
  skuStatus?: string; // 实际JSON中的SKU状态
  displayQty?: number; // 实际JSON中的显示数量

  // 保持向下兼容的旧字段
  selectedSku?: {
    skuId: string;
    skuName: string;
    attributes: Array<{
      attributeName: string;
      attributeValue: string;
      attributeId: string;
    }>;
  };
  availableSkus?: Array<{
    skuId: string;
    skuName: string;
    price: string;
    stock: number;
    attributes: Array<{
      attributeName: string;
      attributeValue: string;
      attributeId: string;
    }>;
  }>;
  skuSelector?: {
    attributeGroups: Array<{
      groupName: string;
      groupId: string;
      options: Array<{
        optionId: string;
        optionName: string;
        optionValue: string;
        available: boolean;
        selected: boolean;
      }>;
    }>;
  };
}

// 新增：配送承诺和地址信息楼层接口
export interface ProductShipPromiseFloor {
  // 实际JSON中的特殊字段名 - 地址承诺信息
  'C-M#viewAddressItem&promise'?: any;
  // 地址核心信息
  'C-M#viewAddressItem&addressCore'?: any;
  // 地址保证信息
  'C-M#viewAddressItem&guarantee'?: any;
  // 配送信息
  'C-M#viewAddressItem&deliveryInfo'?: any;
  // 配送选项
  'C-M#viewAddressItem&shipmentOptions'?: any;

  // 保持向下兼容的传统字段
  deliveryPromise?: {
    promiseText: string;
    estimatedDeliveryTime: string;
    deliveryOptions: Array<{
      optionId: string;
      optionName: string;
      deliveryTime: string;
      deliveryFee: string;
      selected: boolean;
    }>;
  };
  addressInfo?: {
    selectedAddress?: {
      addressId: string;
      fullAddress: string;
      recipientName: string;
      phoneNumber: string;
      isDefault: boolean;
    };
    availableAddresses?: Array<{
      addressId: string;
      fullAddress: string;
      recipientName: string;
      phoneNumber: string;
      isDefault: boolean;
    }>;
  };
  shipmentInfo?: {
    supportedMethods: string[];
    restrictions?: string[];
    specialInstructions?: string;
  };
}

export interface FloorData {
  // BreadCrumb Floor
  'C-M#viewBreadCrumbItem&core'?: {
    category1Id: string;
    category1Name: string;
    category2Id: string;
    category2Name: string;
    category3Id: string;
    category3Name: string;
  };

  // Navigation Floor - 增强导航按钮结构
  navigationButtons?: NavigationButtons;
  // 实际JSON中使用的导航按钮字段
  buttons?: Array<{
    text: string;
    link: string;
    target: string;
  }>;
  'C-M#priceViewItem&followInfo'?: {
    followState: boolean;
  };
  title?: string;

  // Product Tag Floor
  productTagPartList?: Array<{
    type: {
      namespace: string;
      version: string;
    };
    info: {
      'C-P#productTag&productTag': {
        tagType: number;
        tagText: string;
      };
    };
  }>;
  'C-M#ViewTitleItem&core'?: {
    title: string;
  };

  // Best Seller Floor
  rank?: number;
  sequenceUuid?: string;
  rankText?: string;
  destinationPageUrl?: string;
  categoryName?: string;
  categoryId?: number;

  // Main Image Floor
  imagePartList?: Array<{
    'C-P#image&image': {
      smallImageUrl: string;
      bigImageUrl: string;
      imageUrl: string;
      supplementaryImageList: any[];
    };
  }>;

  // Promotion Floor - 完善促销信息结构
  promotionPartList?: Array<{
    'C-P#promotionInfo&promotionCore': {
      promotionType: string;
      promotionId: string;
    };
    'C-P#promotionInfo&promotionBasic': {
      formattedStartTime: string;
      promotionTypeTag: string;
      formattedEndTime: string;
      promotionTagsTitle: number[];
      startTime: number;
      promotionTag: string;
      endTime: number;
      promotionText: string;
    };
    'C-P#promotionInfo&priceInfo': {
      mainPrice: string;
    };
    'C-P#promotionInfo&textInfo': {
      subextinfo: string;
    };
  }>;
  // 新增：优惠券部分列表
  couponPartList?: Array<{
    couponInfo: CouponInfo;
    displayText: string;
    actionText: string;
    available: boolean;
  }>;

  // Price Floor - 增强价格信息
  percentReductionDesc?: string | null;
  crossOffPrice?: string;
  mainPrice?: string;
  'C-M#priceViewItem&priceInfo'?: {
    unitPrice: string;
    finalPricePop: {
      popupTitle: string;
      hintText: string;
      detailList: Array<{
        titleText: string;
        price: string;
        sign: string | null;
        priceColor: string;
      }>;
      summaryText: string;
      summaryPrice: string;
    };
    percentReductionDescIntergation: string | null;
    coupon: string;
    unitPriceDesc: string;
    mainPrice: string;
    crossOffPrice: string;
    discount: string;
    finalPrice: string;
    promotion: string;
  };

  // Unit Price Floor
  unitPrice?: string;
  unitPriceDescIntergation?: string;

  // Payment Floor - 完善支付信息
  popUpText?: string;
  payTextIntergation?: string;
  paymentOptions?: Array<{
    paymentId: string;
    paymentName: string;
    paymentIcon: string;
    available: boolean;
    description?: string;
  }>;
  installmentInfo?: {
    available: boolean;
    options?: Array<{
      periods: number;
      monthlyAmount: string;
      totalAmount: string;
      interestRate: string;
    }>;
  };

  // Bottom Bar Floor
  bottomBarButtons?: BottomBarButtons;
  bizType?: string;
  unsupportedPurchaseInfo?: {
    noAddressText: string;
  };

  // 新增楼层数据字段
  // 评价销量数据楼层
  productCommentSaleFloor?: ProductCommentSaleFloor;

  // 资产信息楼层（优惠券、礼品、样品等）
  productAssetsFloor?: ProductAssetsFloor;

  // SKU选择信息楼层
  productSelectedFloor?: ProductSelectedFloor;

  // 配送承诺和地址信息楼层
  productShipPromiseFloor?: ProductShipPromiseFloor;

  // 底部操作栏楼层
  productBottomBarFloor?: ProductBottomBarFloor;

  // 通用信息楼层
  productCommonFloor?: ProductCommonFloor;

  // Common Floor
  'VCYBB2C-D#slashBottom&basic'?: {
    cykj: string;
  };
  shareInfo?: {
    shareTitle: string;
    shareLink: string;
  };
  deliveryInfo?: {
    fulfillmentMethodList: Array<{
      icon: string;
      title: string;
      deliveryTime: string | null;
      price: string;
      shipmentType: string;
      selectedStatus: boolean;
    }>;
    mainTitle: string;
  };
  'C-M#viewCommonItem&commonCore'?: {
    noSaleFlag: boolean;
    saleAmount: string;
    delivery: string;
    isBigItem: boolean;
    pay_first: string;
    venderId: string;
    skuStatus: string;
    availableNum: number;
    sptxbq: string;
    stockState: number;
    warranty: string;
    model: string;
    spuId: string;
    skuId: string;
  };
  autoClaimCoupon?: {
    enable: boolean;
    couponList: any[];
  };
  'C-M#retailProductItem&retail'?: {
    ShipmentStatus: boolean;
    PickupStatus: boolean;
    subscriptionStatus: boolean;
  };
  'C-M#viewCommonItem&commonBasic'?: {
    isXnzt: boolean;
    noSingleBuy: boolean;
    property: {
      delivery: string;
      pay_first: string;
      warranty: string;
      model: string;
      kjsp: string | null;
      sptxbq: string;
      fwsp: string | null;
      sfhVAT: string | null;
    };
    isCanUsePoint: boolean;
    isLoc: boolean;
  };
}

export interface Button {
  btnId: string;
  btnName: string;
  btnNameKey: string | null;
  extInfos: {
    [key: string]: any;
  };
}

export interface AccessoryConfig extends PromotionInfo {
  popTitle: string;
  /** 描述文案key（如 'sample.desc'、'gift.desc'），i18n多语言配置 */
  desc: string; // e.g. 'sample.desc' | 'gift.desc'
  /** 右侧按钮文案key（如 'sample.viewSamples'、'gift.viewGifts'） */
  useStatus: string; // e.g. 'sample.viewSamples' | 'gift.viewGifts'
  promotionId: string;
}
// Product Bottom Bar Floor - 底部操作栏楼层
export interface ProductBottomBarFloor extends Omit<FloorData, 'buttons'> {
  // 价格信息相关字段
  'C-M#priceViewItem&priceInfo'?: {
    unitPrice: string;
    finalPricePop: {
      popupTitle: string;
      hintText: string;
      detailList: Array<{
        titleText: string;
        price: string;
        sign: string | null;
        priceColor: string;
      }>;
      summaryText: string;
      summaryPrice: string;
    };
    percentReductionDescIntergation: string | null;
    coupon: string;
    unitPriceDesc: string;
    mainPrice: string;
    crossOffPrice: string;
    discount: string;
    finalPrice: string;
    promotion: string;
  };

  // 按钮配置相关字段
  'C-M#viewBottomBarItem&bottomBarCore'?: {
    buttons: Array<{
      btnId: string;
      btnName: string;
      btnNameKey: string | null;
      extInfos: {
        [key: string]: any;
      };
    }>;
    leftButtons: Array<{
      btnId: string;
      btnName: string;
      btnNameKey: string | null;
      extInfos: {
        [key: string]: any;
      };
    }>;
    rightButtons: Array<{
      btnId: string;
      btnName: string;
      btnNameKey: string | null;
      extInfos: {
        [key: string]: any;
      };
    }>;
  };

  // 库存状态相关字段
  'C-M#viewCommonItem&commonCore'?: {
    noSaleFlag: boolean;
    saleAmount: string;
    delivery: string;
    isBigItem: boolean;
    pay_first: string;
    venderId: string;
    skuStatus: string;
    availableNum: number;
    sptxbq: string;
    stockState: number;
    warranty: string;
    model: string;
    spuId: string;
    skuId: string;
  };

  // 向下兼容的旧字段
  mainPrice?: string;
  crossOffPrice?: string;
  percentReductionDesc?: string | null;
  buttons?: Button[];
}

// Product Common Floor - 通用产品信息楼层
export interface ProductCommonFloor extends FloorData {
  // 通用核心信息
  'C-M#viewCommonItem&commonCore'?: {
    noSaleFlag: boolean;
    saleAmount: string;
    delivery: string;
    isBigItem: boolean;
    pay_first: string;
    venderId: string;
    skuStatus: string;
    skuShelvesStockStatus: string;
    availableNum: number;
    sptxbq: string;
    stockState: number;
    warranty: string;
    model: string;
    spuId: string;
    skuId: string;
  };

  // 通用基础信息
  'C-M#viewCommonItem&commonBasic'?: {
    isXnzt: boolean;
    noSingleBuy: boolean;
    property: {
      delivery: string;
      pay_first: string;
      warranty: string;
      model: string;
      kjsp: string | null;
      sptxbq: string;
      fwsp: string | null;
      sfhVAT: string | null;
    };
    isCanUsePoint: boolean;
    isLoc: boolean;
  };

  // 零售产品信息
  'C-M#retailProductItem&retail'?: {
    ShipmentStatus: boolean;
    PickupStatus: boolean;
    subscriptionStatus: boolean;
  };

  // 配送信息
  deliveryInfo?: {
    fulfillmentMethodList: Array<{
      icon: string;
      title: string;
      deliveryTime: string | null;
      price: string;
      shipmentType: string;
      selectedStatus: boolean;
    }>;
    mainTitle: string;
  };

  // 分享信息
  shareInfo?: {
    shareTitle: string;
    shareDesc: string;
    shareLink: string;
  };

  // 自动领取优惠券
  autoClaimCoupon?: {
    enable: boolean;
    couponList: any[];
  };

  // 向下兼容的旧字段
  title?: string;
  mainPrice?: string;
  crossOffPrice?: string;
  percentReductionDesc?: string | null;
}

export interface fulfillmentMethodListProps {
  deliveryTime?: string;
  icon: string;
  priceIntergation?: string;
  selectedStatus?: boolean;
  shipmentType?: string;
  title: string;
}

export enum CouponStatusType {
  CouponApply = 1, //展示对勾
}

/** 促销类型枚举 */
export enum PROMOTION_TYPE {
  /** 预售 */
  PRESALE = -8,
  /** 秒杀 */
  FLASH_SALE = 2,
  /** 新人促销 */
  NEW_USER = 51,
  /** HappyHour */
  HAPPY_HOUR = 52,
}
